import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  EmptyState,
  FeatureGate,
  Screen,
  Text,
  TextInput,
} from '~/atomic';
import { cn } from '~/utils/cn';
import { computeInstallmentAmortization } from '~/utils/amortization';
import { parseAmountToCents } from '~/utils/contractLabels';
import { formatCentsToBRL } from '~/utils/formatFinance';
import { formatDateLong } from '~/utils/contractLabels';
import { maskMoney } from '~/utils/masks';
import modalStatusService from '~/services/modalStatus';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectContractById,
  selectContracts,
  selectInstallmentsForContract,
} from '~/store/contracts/selectors';
import {
  fetchContractDetails,
  markInstallmentPaid,
  reverseInstallmentPayment,
} from '~/store/contracts/thunks';
import type { ContractInstallment } from '~/store/contracts/interfaces';

const DAY = 24 * 60 * 60 * 1000;

const STATUS_LABEL: Record<ContractInstallment['status'], string> = {
  paid: 'Paga',
  next: 'A vencer',
  future: 'Futura',
  overdue: 'Vencida',
};

const STATUS_CLASS: Record<ContractInstallment['status'], string> = {
  paid: 'bg-success-01',
  next: 'bg-alert-01',
  future: 'bg-intense-02',
  overdue: 'bg-warning-01',
};

const STATUS_TEXT: Record<ContractInstallment['status'], string> = {
  paid: 'color-success-05',
  next: 'color-alert-06',
  future: 'color-intense-06',
  overdue: 'color-warning-05',
};

function MetricRow({
  label,
  value,
  isLast,
}: {
  label: string;
  value: string;
  isLast?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between py-smaller',
        !isLast && 'border-b border-intense-03',
      )}
    >
      <Text className="text-bodyMD color-intense-06">{label}</Text>
      <Text className="ml-nano text-right flex-1 text-bodyLG color-complement-01">
        {value}
      </Text>
    </div>
  );
}

interface FormValues {
  amount: string;
}

export default function InstallmentDetailsView() {
  const { id, installmentId } = useParams<{ id: string; installmentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { saving } = useAppSelector(selectContracts);
  const contract = useAppSelector(selectContractById(id ?? ''));
  const installments = useAppSelector(selectInstallmentsForContract(id ?? ''));
  const installment = useMemo(
    () => installments.find((i) => i.id === installmentId),
    [installments, installmentId],
  );
  const isPaid = installment?.status === 'paid';
  const [loading, setLoading] = useState(false);

  const showBulkPayShortcut = useMemo(
    () => installments.filter((i) => i.status !== 'paid').length > 1,
    [installments],
  );

  useEffect(() => {
    if (id && (!installments.length || !installment)) {
      dispatch(fetchContractDetails(id));
    }
  }, [dispatch, id, installments.length, installment]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      amount: installment ? maskMoney(String(installment.amount)) : '',
    },
  });

  useEffect(() => {
    if (installment) {
      reset({ amount: maskMoney(String(installment.amount)) });
    }
  }, [installment, reset]);

  const metrics = useMemo(() => {
    if (!contract || !installment) return null;
    const total = contract.amountTotal || 0;
    const share = total > 0 ? installment.amount / total : 0;
    const amort = computeInstallmentAmortization(contract, installment);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const due = new Date(installment.dueDate);
    const daysDiff = Math.round((due.getTime() - start.getTime()) / DAY);
    return {
      sharePct: share * 100,
      estimatedInterestCents: amort.estimatedInterestCents,
      principalPortionCents: amort.principalPortionCents,
      paidAmountCents: amort.paidAmountCents,
      interestPaidCents: amort.interestPaidCents,
      interestDiscountCents: amort.interestDiscountCents,
      daysDiff,
    };
  }, [contract, installment]);

  if (!contract || !installment) {
    return (
      <Screen headerTitle="Parcela">
        <EmptyState
          icon="icon-document"
          title="Parcela não encontrada"
          description="Essa parcela pode ter sido removida. Volte e tente novamente."
        />
      </Screen>
    );
  }

  const situation = (() => {
    if (isPaid) {
      return installment.paidAt
        ? `Paga em ${formatDateLong(installment.paidAt)}`
        : 'Paga';
    }
    const days = metrics?.daysDiff ?? 0;
    if (days < 0) return `${Math.abs(days)} dia(s) em atraso`;
    if (days === 0) return 'Vence hoje';
    return `Vence em ${days} dia(s)`;
  })();

  const handleConfirmPayment = handleSubmit(async (values) => {
    const amountCents = parseAmountToCents(values.amount);
    if (!amountCents || amountCents <= 0) {
      modalStatusService.show({
        type: 'error',
        title: 'Valor inválido',
        description: 'Informe o valor pago para registrar a parcela.',
        textFirstButton: 'Entendi',
        showFirstButton: true,
        onClickFirstButton: modalStatusService.hide,
        handleCloseModal: modalStatusService.hide,
      });
      return;
    }
    setLoading(true);
    const ok = await dispatch(
      markInstallmentPaid({
        contractId: contract.id,
        installmentId: installment.id,
        amountCents,
      }),
    ).unwrap();
    setLoading(false);
    if (ok) navigate(-1);
  });

  const handleUndoPayment = () => {
    if (!installment.paymentId) return;
    modalStatusService.show({
      type: 'alert',
      title: 'Desfazer pagamento?',
      description:
        'A parcela voltará para "a vencer" e o saldo do contrato será recalculado.',
      textFirstButton: 'Desfazer',
      textSecondButton: 'Cancelar',
      showFirstButton: true,
      showSecondButton: true,
      onClickFirstButton: async () => {
        modalStatusService.hide();
        const ok = await dispatch(
          reverseInstallmentPayment({
            contractId: contract.id,
            installmentId: installment.id,
            paymentId: installment.paymentId as string,
          }),
        ).unwrap();
        if (ok) navigate(-1);
      },
      onClickSecondButton: modalStatusService.hide,
      handleCloseModal: modalStatusService.hide,
    });
  };

  return (
    <Screen
      headerTitle={`Parcela ${installment.index} de ${contract.installmentsTotal}`}
    >
      <div className="px-small pb-medium pt-medium">
        <div className="mt-small rounded-medium bg-intense-01 border border-intense-03 p-medium flex flex-col items-center text-center">
          <Text className="text-captionLG color-intense-06 uppercase">
            Valor da parcela
          </Text>
          <Text className="mt-quark text-display1 color-complement-01">
            {formatCentsToBRL(installment.amount)}
          </Text>
          <span
            className={cn(
              'mt-smaller rounded-full px-small py-quark',
              STATUS_CLASS[installment.status],
            )}
          >
            <Text className={cn('text-captionLG', STATUS_TEXT[installment.status])}>
              {STATUS_LABEL[installment.status]}
            </Text>
          </span>
        </div>

        <div className="mt-medium rounded-medium bg-intense-01 border border-intense-03 px-small">
          <MetricRow label="Vencimento" value={formatDateLong(installment.dueDate)} />
          <MetricRow label="Situação" value={situation} />
          <MetricRow
            label="Representa do contrato"
            value={`${(metrics?.sharePct ?? 0).toFixed(1).replace('.', ',')}%`}
          />
          <MetricRow
            label="Juros estimado na parcela"
            value={formatCentsToBRL(metrics?.estimatedInterestCents ?? 0)}
          />
          <MetricRow
            label="Amortização"
            value={formatCentsToBRL(metrics?.principalPortionCents ?? 0)}
            isLast={!isPaid}
          />
          {isPaid && metrics ? (
            <>
              <MetricRow
                label="Valor pago"
                value={formatCentsToBRL(metrics.paidAmountCents)}
              />
              <MetricRow
                label="Juros pago"
                value={formatCentsToBRL(metrics.interestPaidCents)}
                isLast={metrics.interestDiscountCents <= 0}
              />
              {metrics.interestDiscountCents > 0 ? (
                <MetricRow
                  label="Desconto de juros"
                  value={formatCentsToBRL(metrics.interestDiscountCents)}
                  isLast
                />
              ) : null}
            </>
          ) : null}
        </div>

        <Text className="mt-quark text-captionMD color-intense-05">
          Juros e amortização são estimativas proporcionais ao valor da parcela.
        </Text>

        {isPaid ? (
          <div className="mt-medium">
            <div className="rounded-small bg-success-01 p-small">
              <Text className="text-bodyLG color-complement-01">
                Pagamento registrado
              </Text>
              <Text className="mt-quark text-bodySM color-intense-06">{situation}</Text>
            </div>
            <button
              type="button"
              onClick={handleUndoPayment}
              className="mt-smaller w-full text-center py-smaller"
            >
              <Text className="text-bodyMD color-warning-05">
                Desfazer pagamento
              </Text>
            </button>
          </div>
        ) : (
          <>
            <FeatureGate feature="installments_pay">
              <form
                onSubmit={handleConfirmPayment}
                className="mt-medium flex flex-col gap-quark"
              >
                <Text className="text-captionLG color-complement-01 mb-nano uppercase">
                  Valor pago
                </Text>
                <TextInput
                  control={control}
                  name="amount"
                  placeholder="R$ 0,00"
                  keyboardType="numeric"
                  mask="money"
                />
                <Text className="text-captionMD color-intense-05">
                  Já preenchido com o valor da parcela. Ajuste se pagou outro
                  valor.
                </Text>
                <Button
                  type="submit"
                  text="Registrar pagamento"
                  rightIcon="icon-check"
                  loading={loading || saving}
                  fullWidth
                  className="mt-medium"
                />
              </form>
            </FeatureGate>

            {showBulkPayShortcut ? (
              <Button
                text="Dar baixa em várias parcelas"
                leftIcon="icon-grid"
                variant="ghost"
                className="mt-smaller border border-primary-01"
                onClick={() =>
                  navigate(
                    `/app/contracts/${contract.id}/installments/bulk-pay`,
                  )
                }
                fullWidth
              />
            ) : null}
          </>
        )}
      </div>
    </Screen>
  );
}
